import React, { useState, useEffect } from 'react';
import { Autocomplete, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField, Typography } from '@mui/material';
import { getData } from '../../Webservice/Webservice';
import { FONT } from './contentShared';
import { AUDIENCE_OPTIONS, Field, GHOST_BTN, ImageUpload, STATUS_OPTIONS, YELLOW_BTN } from './formShared';

const TYPE_OPTIONS = [
  { value: 'subcategory', label: 'Subcategory' },
  { value: 'product', label: 'Product' },
  { value: 'external', label: 'External link' },
];
const emptyBanner = { image: null, type: '', link: '', externalUrl: '', status: 'active', showTo: 'all', position: 1 };

const BannerModal = ({ open, handleClose, handleSubmit, isEdit, editingBanner }) => {
  const [bannerData, setBannerData] = useState(emptyBanner);
  const [options, setOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // what a subcategory / product banner can point to
  useEffect(() => {
    if (bannerData.type !== 'subcategory' && bannerData.type !== 'product') {
      setOptions([]);
      return undefined;
    }
    let cancelled = false;
    setLoadingOptions(true);
    const raw = sessionStorage.getItem('token');
    const token = raw ? `Bearer ${raw}` : '';
    const route = bannerData.type === 'subcategory' ? 'subcategories/list?limit=1000' : 'product/list?isFree=false&limit=1000';
    getData(route, null, token)
      .then((res) => {
        if (cancelled) return;
        const list = bannerData.type === 'subcategory' ? res?.subcategories : res?.products;
        setOptions(Array.isArray(list) ? list.map((x) => ({ id: x._id, title: x.title })) : []);
      })
      .catch(() => { if (!cancelled) setOptions([]); })
      .finally(() => { if (!cancelled) setLoadingOptions(false); });
    return () => { cancelled = true; };
  }, [bannerData.type]);

  useEffect(() => {
    setErrors({});
    if (isEdit && editingBanner) {
      setBannerData({ ...emptyBanner, ...editingBanner, link: editingBanner.link || '', externalUrl: editingBanner.externalUrl || '', showTo: editingBanner.showTo || 'all' });
    } else {
      setBannerData(emptyBanner);
    }
  }, [isEdit, editingBanner, open]);

  const set = (patch) => setBannerData((prev) => ({ ...prev, ...patch }));
  const clear = (key) => errors[key] && setErrors((prev) => ({ ...prev, [key]: undefined }));
  const handleChange = (e) => { set({ [e.target.name]: e.target.value }); clear(e.target.name); };

  const handleFile = (file) => {
    if (!file) return;
    set({ image: file, imagePreview: URL.createObjectURL(file) });
    clear('image');
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const next = {};
    if (!bannerData.image) next.image = 'A banner image is required';
    if (!bannerData.type) next.type = 'Choose where this banner leads';
    if ((bannerData.type === 'subcategory' || bannerData.type === 'product') && !bannerData.link) next.link = `Choose a ${bannerData.type}`;
    if (bannerData.type === 'external' && !/^https?:\/\/\S+/i.test(bannerData.externalUrl || '')) next.externalUrl = 'Enter a full link starting with http:// or https://';
    if (!bannerData.position || Number(bannerData.position) < 1) next.position = 'Enter a position of 1 or more';
    setErrors(next);
    if (Object.keys(next).length) return;
    setSaving(true);
    try {
      await handleSubmit(bannerData);
    } finally {
      setSaving(false);
    }
  };

  const preview = bannerData.imagePreview || (typeof bannerData.image === 'string' ? bannerData.image : null);
  const selected = options.find((o) => o.id === bannerData.link) || null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
      <DialogTitle sx={{ pb: 0.5 }}>
        <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 20, color: '#0f172a' }}>{isEdit ? 'Edit Banner' : 'Create Banner'}</Typography>
        <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b' }}>A promotional image on the home screen that opens a page when tapped.</Typography>
      </DialogTitle>
      <Box component="form" onSubmit={onSubmit}>
        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={2.5}>
            <Grid item xs={12}>
              <ImageUpload src={preview} onFile={handleFile} label="Upload banner image" error={errors.image} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Field
                label="Opens"
                name="type"
                value={bannerData.type}
                onChange={(e) => { set({ type: e.target.value, link: '', externalUrl: '' }); clear('type'); }}
                options={TYPE_OPTIONS}
                required
                error={!!errors.type}
                helperText={errors.type}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              {bannerData.type === 'external' ? (
                <TextField fullWidth size="small" label="Link (URL)" name="externalUrl" value={bannerData.externalUrl} onChange={handleChange} placeholder="https://" error={!!errors.externalUrl} helperText={errors.externalUrl} />
              ) : bannerData.type ? (
                <Autocomplete
                  options={options}
                  loading={loadingOptions}
                  value={selected}
                  getOptionLabel={(o) => o.title || ''}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  onChange={(_, v) => { set({ link: v?.id || '' }); clear('link'); }}
                  renderInput={(params) => <TextField {...params} size="small" label={bannerData.type === 'product' ? 'Product' : 'Subcategory'} error={!!errors.link} helperText={errors.link} />}
                />
              ) : (
                <TextField fullWidth size="small" label="Link" disabled helperText="Choose what the banner opens first" />
              )}
            </Grid>

            <Grid item xs={12} sm={4}>
              <Field label="Position" name="position" type="number" inputProps={{ min: 1 }} value={bannerData.position} onChange={handleChange} required error={!!errors.position} helperText={errors.position} />
            </Grid>
            <Grid item xs={6} sm={4}>
              <Field label="Status" name="status" value={bannerData.status} onChange={handleChange} options={STATUS_OPTIONS} />
            </Grid>
            <Grid item xs={6} sm={4}>
              <Field label="Show to" name="showTo" value={bannerData.showTo} onChange={handleChange} options={AUDIENCE_OPTIONS} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1.5 }}>
          <Button variant="outlined" onClick={handleClose} sx={GHOST_BTN}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={saving} sx={YELLOW_BTN}>{saving ? 'Saving…' : isEdit ? 'Update' : 'Create banner'}</Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default BannerModal;
